/**
 * TableInfoHeader Component
 *
 * A header row for tables showing count/info on the left and action buttons on the right.
 * Used above DataTable for showing "Total: X items" and column/export controls.
 */

import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii } from '../../tokens';

export interface TableInfoHeaderProps {
    /** Info text (e.g., "Total: 27 cuts") */
    infoText?: React.ReactNode;
    /** Right-side actions (buttons, etc.) */
    actions?: React.ReactNode;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const TableInfoHeader: React.FC<TableInfoHeaderProps> = ({ infoText, actions, sx }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
    <Box
        sx={{
            display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1.5 : 0,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${colors.neutral[200]}`,
            backgroundColor: colors.neutral[0],
            flexShrink: 0,
            ...sx,
        }}>
        {/* Left side: Info text */}
        <Box>
            {typeof infoText === 'string' ? (
                <Typography
                    sx={{
                        fontSize: typographyTokens.fontSize.sm,
                        color: colors.neutral[700],
                        fontWeight: typographyTokens.fontWeight.medium,
                        '& strong': {
                            fontWeight: typographyTokens.fontWeight.bold,
                            color: colors.neutral[900],
                        },
                    }}>
                    {infoText}
                </Typography>
            ) : (
                infoText
            )}
        </Box>

        {/* Right side: Actions */}
            {actions && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: isMobile ? '100%' : 'auto',
                        '& > *': isMobile
                            ? {
                                  flex: '1 1 auto',
                                  minWidth: 'fit-content',
                              }
                            : {},
                    }}>
                    {actions}
                </Box>
            )}
    </Box>
);
};

export default TableInfoHeader;
